import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { X, Loader2, Check, Edit2 } from 'lucide-react';
import type { LVProduct } from '@/features/product-library/types';
import { CategoryIconRenderer } from '@/features/product-library/utils/categoryIcons';

interface GroupProductLibraryListProps {
  products: LVProduct[];
  isLoading?: boolean;
  onRemoveProduct: (productId: string) => void;
  onUpdateProductName: (params: { productId: string; name: string }) => void;
  isRemovingProduct?: boolean;
  isUpdatingName?: boolean;
}

export function GroupProductLibraryList({
  products,
  isLoading = false,
  onRemoveProduct,
  onUpdateProductName,
  isRemovingProduct = false,
  isUpdatingName = false,
}: GroupProductLibraryListProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  const handleStartEdit = (product: LVProduct) => {
    setEditingId(product.id);
    setEditName(product.name);
  };

  const handleSaveEdit = async (productId: string) => {
    if (editName.trim()) {
      await onUpdateProductName({ productId, name: editName.trim() });
      setEditingId(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditName('');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="p-4 bg-muted/50 rounded-lg text-center">
        <p className="text-sm text-muted-foreground">
          No products in this group yet. Click "Add Products" to add products from the library.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2 max-h-[400px] overflow-y-auto">
      {products.map(product => {
        const isEditing = editingId === product.id;

        return (
          <div
            key={product.id}
            className="group flex items-center justify-between p-3 bg-background border rounded-lg hover:bg-muted/50 transition-colors"
          >
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                <CategoryIconRenderer
                  category={product.category}
                  size={20}
                  className="text-muted-foreground"
                />
              </div>
              <div className="min-w-0 flex-1">
                {isEditing ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={editName}
                      onChange={e => setEditName(e.target.value)}
                      onKeyDown={e => {
                        e.stopPropagation(); // Prevent space-to-pan interference
                        if (e.key === 'Enter') {
                          handleSaveEdit(product.id);
                        } else if (e.key === 'Escape') {
                          handleCancelEdit();
                        }
                      }}
                      onKeyUp={e => e.stopPropagation()}
                      className="h-7 text-sm"
                      autoFocus
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSaveEdit(product.id)}
                      disabled={isUpdatingName}
                      className="h-7 w-7 p-0"
                    >
                      <Check className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleCancelEdit}
                      className="h-7 w-7 p-0"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-sm truncate">{product.name}</p>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleStartEdit(product)}
                      className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Edit2 className="h-3 w-3" />
                    </Button>
                  </div>
                )}
                {product.product && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>
                      {product.product.brand} {product.product.model}
                    </span>
                    <Badge variant="secondary" className="capitalize text-xs">
                      {product.category.replace(/_/g, ' ')}
                    </Badge>
                  </div>
                )}
              </div>
            </div>

            {!isEditing && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onRemoveProduct(product.id)}
                disabled={isRemovingProduct}
                className="h-8 w-8 p-0 shrink-0"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        );
      })}
    </div>
  );
}
